import React, { useEffect } from 'react';
import Services from '../components/Services';
import Faq from '../components/Faq';
import { User, Users, Home, MapPin, Zap, Search, Shield, CheckCircle, MessageSquare, Clock, ArrowRight, Route, Wrench, ShieldCheck, ServerCog, PhoneCall } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { APP_CONFIG } from '../config/app';

import { contactCtaClick } from '../utils/analytics';
import TrackedContactLink from '../components/TrackedContactLink';
const DepannageInformatique: React.FC = () => {
  const location = useLocation();
  const whatsappUrl = APP_CONFIG.contact.whatsappUrl;

  useEffect(() => {
    document.title = 'Dépannage informatique à domicile et sur site | SOS IT Services';
    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Dépannage informatique à domicile et sur site autour de Viry et Genève : PC lent, virus, panne réseau, Windows/Mac. Diagnostic gratuit, déplacement inclus.');
    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://sos-it-services.fr/depannage-informatique');
    // OG tags
    const ogTags: Record<string, string> = {
      'og:title': 'Dépannage informatique à domicile et sur site | SOS IT Services',
      'og:description': 'PC lent, virus, panne réseau — j\'interviens chez vous ou dans vos locaux autour de Viry et Genève. Diagnostic gratuit, devis avant intervention.',
      'og:url': 'https://sos-it-services.fr/depannage-informatique',
      'og:image': 'https://sos-it-services.fr/images/heroes/depan-hero.png',
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });
    window.scrollTo(0, 0);

    const hash = location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const howItWorksSteps = [
    {
      id: '01',
      title: 'Décrivez votre problème',
      description: 'WhatsApp ou espace client : vous expliquez votre souci en 2 minutes, diagnostic gratuit.',
      icon: MessageSquare,
      delay: 0,
    },
    {
      id: '02',
      title: 'Un expert vous répond',
      description: "Je vous réponds en moins de 2h avec un premier plan d'action clair, et j'interviens souvent le jour même.",
      icon: Clock,
      delay: 0.08,
    },
    {
      id: '03',
      title: "Planification de l'intervention",
      description: "Nous convenons ensemble d'un créneau pour une intervention à votre domicile (particuliers) ou sur site (entreprises).",
      icon: Route,
      delay: 0.16,
    },
    {
      id: '04',
      title: 'Problème résolu',
      description: 'Devis validé avant intervention, déplacement inclus et garantie 90 jours.',
      icon: CheckCircle,
      delay: 0.24,
    },
  ] as const;

  const revealUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0 },
  };

  const proOffers = [
    {
      title: 'Intervention ponctuelle',
      description: "Pour une panne urgente ou un besoin ponctuel sur site.",
      badge: 'Rapide',
      icon: Wrench,
      accent: 'from-accent/20 to-transparent',
    },
    {
      title: 'Suivi mensuel',
      description: 'Un accompagnement régulier pour stabiliser votre parc et prévenir les incidents.',
      badge: 'Le plus choisi',
      icon: ServerCog,
      accent: 'from-accent/15 to-transparent',
    },
    {
      title: 'Audit & sécurisation',
      description: 'Analyse de votre infrastructure, recommandations claires et plan de priorités.',
      badge: 'Stratégique',
      icon: ShieldCheck,
      accent: 'from-accent/10 to-transparent',
    },
  ] as const;

  const proNeeds = ['Pannes urgentes', 'Réseau lent', 'Microsoft 365', 'Sauvegardes', 'Sécurité', 'Maintenance des postes'] as const;

  return (
    <div className="pt-header md:pt-header-md">

      {/* Hero */}
      <div className="relative py-20 min-h-[420px] flex items-center overflow-hidden bg-surface">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/heroes/depan-hero.webp"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-surface/70" />
        </div>

        <div className="relative z-10 container-site">
          <motion.div
            className="text-center"
            variants={revealUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          >
            <motion.p
              className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent-strong/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent-soft mb-4"
              animate={{ boxShadow: ['0 0 0 rgba(88,101,242,0)', '0 0 24px rgba(88,101,242,0.25)', '0 0 0 rgba(88,101,242,0)'] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
            >
              Diagnostic gratuit en 2 minutes
            </motion.p>
            <h1 className="heading-display mb-6">
              Panne informatique ? <span className="text-accent-soft">Je vous dépanne rapidement</span>
            </h1>
            <p className="text-fg text-base md:text-lg max-w-3xl mx-auto mb-8">
              Particulier ou entreprise : décrivez votre problème en quelques messages et un technicien intervient à domicile ou sur site autour de Viry et Genève (Pays de Gex, Haute-Savoie).
            </p>

            {/* Badges réassurance */}
            <motion.div
              className="flex flex-wrap items-center justify-center gap-3 mb-10"
              variants={revealUp}
              transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
            >
              <span className="flex items-center gap-1.5 bg-white/12 border border-line-strong rounded-full px-4 py-1.5 text-sm text-fg">
                <Zap className="h-4 w-4 text-accent-soft" /> Réponse en moins de 2h
              </span>
              <span className="flex items-center gap-1.5 bg-white/12 border border-line-strong rounded-full px-4 py-1.5 text-sm text-fg">
                <Search className="h-4 w-4 text-accent-soft" /> Diagnostic gratuit
              </span>
              <span className="flex items-center gap-1.5 bg-white/12 border border-line-strong rounded-full px-4 py-1.5 text-sm text-fg">
                <Shield className="h-4 w-4 text-accent-soft" /> Garantie 90 jours
              </span>
            </motion.div>

            {/* CTAs */}
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              variants={revealUp}
              transition={{ duration: 0.5, delay: 0.16, ease: 'easeOut' }}
            >
              <motion.a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => contactCtaClick({ channel: 'whatsapp', service: 'depannage', cta_location: 'hero' })}
                className="inline-flex items-center px-6 py-3 bg-accent-strong hover:bg-accent-hover text-white font-bold rounded-full transition-colors duration-200"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Je veux être dépanné maintenant <ArrowRight className="ml-2 h-4 w-4" />
              </motion.a>
              <motion.a
                href="#particuliers"
                className="inline-flex items-center px-6 py-3 bg-white/5 border border-line text-fg font-medium rounded-lg hover:bg-white/10 transition-all duration-300"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Voir mes interventions
              </motion.a>
            </motion.div>

            <p className="mt-4 text-sm text-fg/70">
              Vous préférez m'écrire ?{' '}
              <RouterLink to={APP_CONFIG.routes.contact} className="inline-flex min-h-11 items-center underline hover:text-fg transition-colors md:min-h-0">
                Formulaire de contact
              </RouterLink>
            </p>

          </motion.div>
        </div>
      </div>

      {/* Comment ça marche */}
      <section className="section-spacing relative bg-canvas overflow-hidden">

        <div className="relative container-site">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/35 bg-accent-strong/10 px-4 py-1.5 text-xs font-semibold tracking-wider text-accent-soft uppercase mb-4">
              Processus express
            </span>
            <h2 className="heading-section mb-2">
              Comment ça <span className="text-accent-soft">marche ?</span>
            </h2>
            <p className="text-fg-muted text-sm">Un parcours simple, clair et rapide en 4 étapes</p>
          </motion.div>

          <div className="relative max-w-5xl mx-auto">
            <div className="hidden md:block absolute top-11 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-accent/35 to-transparent" />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {howItWorksSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.id} className="relative">
                    {/* Mobile vertical connector */}
                    {index < howItWorksSteps.length - 1 && (
                      <div className="md:hidden absolute -bottom-3 left-1/2 -translate-x-1/2 w-px h-6 bg-gradient-to-b from-accent/40 to-transparent z-10" />
                    )}
                  <motion.div
                    className="group relative text-center p-6 md:p-7 rounded-2xl border border-line bg-surface backdrop-blur-sm"
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.35 }}
                    transition={{ duration: 0.55, delay: step.delay, ease: 'easeOut' }}
                    whileHover={{ y: -6, scale: 1.01, transition: { duration: 0.22, ease: 'easeOut' } }}
                  >
                    <motion.div
                      className="absolute inset-0 rounded-2xl bg-gradient-to-br from-accent/20 via-transparent to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      aria-hidden="true"
                    />

                    <div className="relative z-10">
                      <motion.div
                        className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-strong/20 border border-accent/35 text-accent-soft"
                        whileHover={{ rotate: [0, -5, 5, 0] }}
                        transition={{ duration: 0.45 }}
                      >
                        <Icon className="h-6 w-6" />
                      </motion.div>

                      <span className="inline-flex rounded-full border border-accent/30 bg-accent-strong/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-soft mb-3">
                        Étape {step.id}
                      </span>

                      <h3 className="text-fg font-semibold mb-2">{step.title}</h3>
                      <p className="text-fg-muted/90 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </motion.div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Séparateur */}
      <div className="h-px bg-gradient-to-r from-transparent via-accent/25 to-transparent" />

      {/* Section particuliers */}
      <section id="particuliers" className="section-spacing bg-canvas border-t border-line">
        <div id="pricing-details" className="container-site">
          <motion.div
            className="flex items-center mb-4"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            <User className="h-6 w-6 text-accent-soft mr-3" />
            <h2 className="heading-section">
              Pour les <span className="text-accent-soft">particuliers</span>
            </h2>
          </motion.div>
          <motion.p
            className="text-fg-muted max-w-3xl mb-8"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.45, delay: 0.05, ease: 'easeOut' }}
          >
            En 10 secondes, décrivez votre besoin : un technicien se déplace chez vous, déplacement inclus, avec diagnostic gratuit et devis avant intervention.
          </motion.p>
          <div className="grid grid-cols-1 gap-5 max-w-2xl">
            <motion.div
              className="bg-surface rounded-xl p-6 border border-line-strong cursor-pointer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.5, delay: 0.08, ease: 'easeOut' }}
              whileHover={{ y: -6, scale: 1.01 }}
            >
              <div className="flex items-center mb-3">
                <div className="bg-accent-strong/20 rounded-full p-2 mr-3 border border-accent/35">
                  <Home className="h-5 w-5 text-accent-soft" />
                </div>
                <h3 className="text-fg font-semibold">Intervention à domicile</h3>
              </div>
              <div className="flex items-center gap-1.5 text-fg-muted text-xs mb-4">
                <MapPin className="h-3 w-3 flex-shrink-0" /> 50 km autour de Genève · Pays de Gex (01) · Haute-Savoie (74)
              </div>
              <p className="text-fg-muted text-sm mb-5">
                Un expert se déplace chez vous avec déplacement inclus, jusqu'à 1h d'intervention, et possibilité d'intervention le jour même selon disponibilités.
              </p>
              <div className="rounded-lg border border-accent/30 bg-accent/10 p-4">
                <p className="text-accent-soft text-xs uppercase tracking-wide mb-2">Tarif à domicile</p>
                <p className="text-fg font-semibold">Sur devis</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Séparateur */}
      <div className="h-px bg-gradient-to-r from-transparent via-accent/20 to-transparent" />

      {/* Section TPE/PME */}
      <section id="tpe-pme" className="section-spacing bg-canvas">
        <div className="container-site">
          <motion.div
            className="flex items-center mb-4"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            <Users className="h-6 w-6 text-accent-soft mr-3" />
            <h2 className="heading-section">
              Pour les <span className="text-accent-soft">TPE/PME</span>
            </h2>
          </motion.div>
          <motion.p
            className="text-fg-muted max-w-3xl mb-6"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.45, delay: 0.05, ease: 'easeOut' }}
          >
            Une maintenance informatique pro pour limiter les interruptions d'activité, sécuriser votre environnement et vous redonner de la visibilité sur vos priorités IT.
          </motion.p>

          <motion.div
            className="flex flex-wrap gap-2 mb-8"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.45, delay: 0.1, ease: 'easeOut' }}
          >
            {proNeeds.map((need) => (
              <span key={need} className="rounded-full border border-line-strong bg-white/5 px-3 py-1.5 text-xs text-fg">
                {need}
              </span>
            ))}
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
            {proOffers.map((offer, index) => {
              const Icon = offer.icon;
              return (
                <motion.div
                  key={offer.title}
                  className="group relative overflow-hidden rounded-xl border border-white/12 bg-surface p-5 cursor-pointer"
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.45, delay: index * 0.07, ease: 'easeOut' }}
                  whileHover={{ y: -6, scale: 1.01 }}
                >
                  <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${offer.accent} opacity-0 transition-opacity duration-300 group-hover:opacity-100`} />
                  <div className="relative">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-line-strong bg-white/10 text-accent-soft">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="rounded-full border border-line-strong bg-white/5 px-2.5 py-1 text-[11px] uppercase tracking-wide text-fg">
                        {offer.badge}
                      </span>
                    </div>
                    <h3 className="text-fg font-semibold mb-2">{offer.title}</h3>
                    <p className="text-sm text-fg-muted leading-relaxed">{offer.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            className="bg-white/5 rounded-xl p-6 border border-line flex flex-col md:flex-row items-center justify-between gap-4"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <div>
              <p className="text-fg font-semibold mb-1">Parlons de vos besoins IT en 15 minutes</p>
              <p className="text-fg-muted text-sm">Audit express, priorités claires, plan d'action concret et proposition adaptée à votre entreprise.</p>
            </div>
            <div className="flex">
              <TrackedContactLink channel="whatsapp" service="depannage" ctaLocation="cta" 
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-5 py-3 bg-accent-strong hover:bg-accent-hover text-white font-bold rounded-full transition-colors duration-200 whitespace-nowrap"
              >
                <PhoneCall className="mr-2 h-4 w-4" />
                Planifier un audit 15 min <ArrowRight className="ml-2 h-4 w-4" />
              </TrackedContactLink>
            </div>
          </motion.div>
        </div>
      </section>

      <Services />

      <Faq showLink={false} showAllQuestions={true} />

      {/* Zones d'intervention locales */}
      <section className="section-spacing bg-canvas border-t border-line">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-fg text-center mb-3"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            Intervention à domicile dans la région de <span className="text-accent-soft">Genève</span>
          </motion.h2>
          <p className="section-intro text-center mb-8">
            J'interviens à domicile et sur site dans les villes suivantes.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {[
              { label: 'Dépannage informatique autour de Genève', href: '/depannage-informatique-geneve' },
              { label: 'Dépannage informatique à Saint-Julien-en-Genevois', href: '/depannage-informatique-saint-julien-en-genevois' },
              { label: 'Dépannage informatique à Viry', href: '/depannage-informatique-viry' },
              { label: 'Dépannage informatique à Valleiry', href: '/depannage-informatique-valleiry' },
              { label: 'Dépannage informatique à Annemasse', href: '/depannage-informatique-annemasse' },
              { label: 'Dépannage informatique à Annecy', href: '/depannage-informatique-annecy' },
              { label: 'Dépannage informatique dans le Pays de Gex', href: '/depannage-informatique-pays-de-gex' },
            ].map(({ label, href }) => (
              <RouterLink
                key={href}
                to={href}
                className="flex min-h-11 items-center gap-2 bg-white/5 hover:bg-accent-strong/20 border border-line hover:border-accent/50 text-fg-muted hover:text-fg px-4 py-2 rounded-full text-sm transition-colors md:min-h-0"
              >
                <MapPin className="w-3.5 h-3.5 text-accent-soft" /> {label}
              </RouterLink>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="section-spacing bg-canvas border-t border-line">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="rounded-2xl border border-accent/35 bg-gradient-to-r from-accent/20 via-accent/10 to-accent/10 p-7 md:p-8 text-center"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <p className="text-accent-soft text-xs uppercase tracking-wider mb-3">Prêt à régler le problème ?</p>
            <h3 className="text-xl md:text-2xl font-bold text-fg mb-3">
              Décrivez votre panne en 2 minutes, je vous réponds en moins de 2h
            </h3>
            <p className="text-fg text-sm md:text-base mb-6">
              Diagnostic gratuit, intervention à domicile ou sur site autour de Viry et Genève.
            </p>
            <TrackedContactLink channel="whatsapp" service="depannage" ctaLocation="cta" 
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-accent-strong hover:bg-accent-hover text-white font-bold rounded-full transition-colors duration-200"
            >
              Je veux être dépanné maintenant <ArrowRight className="ml-2 h-4 w-4" />
            </TrackedContactLink>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DepannageInformatique;
